import { assert } from '@ember/debug';
import { computed, get } from '@ember/object';
import type ComputedProperty from '@ember/object/computed';
import { isBlank } from '@ember/utils';
import type DeviceModel from 'garaje/models/device';

/**
 * Compare the two app versions.
 * Returns -1 if the first is lower than the second, 1 if it is greater, or 0 if they are the same.
 * Useful for passing to Array.prototype.sort().
 *
 * @function
 * @param a - app version (in "X.Y.Z" format)
 * @param b - app version (in "X.Y.Z" format)
 */
function appVersionComparator(a: string, b: string): number {
  const isValid = isAppVersionValid(a) && isAppVersionValid(b);
  assert('App versions must be valid.', isValid);

  const aParts = a.split('.').map((number) => parseInt(number, 10));
  const bParts = b.split('.').map((number) => parseInt(number, 10));

  // NaN for a part the shorter version does not have keeps both comparisons false
  for (let index = 0; index < aParts.length; index++) {
    if ((aParts[index] ?? NaN) > (bParts[index] ?? NaN)) {
      return 1;
    }
    if ((aParts[index] ?? NaN) < (bParts[index] ?? NaN)) {
      return -1;
    }
  }

  return 0;
}

/**
 * Higher order function that returns a computed property that can be used to check if an app update is required
 *
 * @function
 * @param dependentKey - dependent key where the iPads are location. `Ex: model.ipads.@each{isIpad,appVersion}`
 * @param minimumIpadVersionNeeded - Number of the iPad version
 */

function isAppUpdateRequired(dependentKey: string, minimumIpadVersionNeeded: string): ComputedProperty<boolean> {
  return computed(`${dependentKey}.@each{isIpad,appVersion}`, function () {
    // return true if at least one iPad does not meet required app version
    const devices = <DeviceModel[]>(get(this, dependentKey) ?? []);
    const versions = devices.filter((ipad) => ipad.isIpad).map((ipad) => ipad.appVersion);

    const areAllIpadVersionsValid = versions.every(isAppVersionValid);
    if (!areAllIpadVersionsValid) {
      return false;
    }
    return versions.some((version) => !isAppVersionGreaterEqual(version, minimumIpadVersionNeeded));
  });
}

/**
 * If the app version is invalid, return false
 * Return true if the current app version is greater or equal than the target app version
 * Otherwise, return false
 *
 * @function
 * @param currentAppVersion - The current app version in string format
 * @param targetAppVersion - The target app version in string format
 */
function isAppVersionGreaterEqual(currentAppVersion: string, targetAppVersion: string): boolean {
  return appVersionComparator(currentAppVersion, targetAppVersion) >= 0;
}

/**
 * Return true if app version string is valid
 *
 * @function
 * @param appVersion - The app version in string format
 */
function isAppVersionValid(appVersion: string | null | undefined): appVersion is string {
  if (isBlank(appVersion)) {
    return false;
  }
  return RegExp(/^\d+(\.\d+){0,2}\.\d+$/).test(<string>appVersion);
}

export { appVersionComparator, isAppVersionValid, isAppVersionGreaterEqual, isAppUpdateRequired };
